import { useEffect } from 'react';
import { startRegistration, startAuthentication, base64URLStringToBuffer, bufferToBase64URLString } from '@simplewebauthn/browser';
import { SelectAddressFromKeypoolSection } from './KeysAndSigningUI';
import { TextLine3 } from './UtilComponents';
import { useGlobalStateSelector, words, stateKeysToSetAddress, stateKeysToSetUserName } from './globalState'
import { generateDicewarePassphraseAsync } from './diceware';
import { canonicalize } from 'json-canonicalize';
import {
  getKeyPairsFromMnemonic,
  getKeyPairAndAddressFromWif,
  getAddressFromKeyNW,
  generateMnemonic,
  signWithKeyNW,
  encryptByKeyCardStyle,
  formatToKeyCardStyle,
  formatToSingleTextRecord,
  walletsAvailable,
  keepKeyPairsInMemory,
} from './keysAndSigning';
import { useNfcrwWrite } from './nativeAppHooks';
import { notifyNativeAppWriteResult } from './nativeApp';
import { NativeAppWriteButton } from './NativeAppUI';
import { handleClickLogin } from './login';
import {
  arrayBufferToBase64,
  postJson,
  devLog,
  wrapErrorWithCause,
  notificationMessageWithError,
  updateCompositeRecordInIndexedDb,
  getCompositeRecordFromIndexedDb,
  deleteCompositeRecordFromIndexedDb,
  writeNdefRecordsOnNfc,
} from './utils';
import iconKeyCard from './image/playing_cards_FILL0_wght400_GRAD0_opsz24.svg';
import iconQr from './image/qr_code_2_FILL0_wght400_GRAD0_opsz24.svg';
import iconKeyPool from './image/key_24dp_000000_FILL0_wght400_GRAD0_opsz24.svg';

// CONFIRME MNEMONIC POPUP ; type: 'confirmMnemonicPopup'
function ConfirmMnemonicPopup(props) {
  const popupLayer = props.popupLayer;
  const popupLayerNext = popupLayer !== undefined ? popupLayer + 1 : 0;
  const popup = useGlobalStateSelector( ({ state }) => state.popup[popupLayer] || {} );
  const hasCamera = useGlobalStateSelector( ({ state }) => state.device?.hasCamera );
  const pass = useGlobalStateSelector( ({ state }) => state.config.clientParameters.specialMessage );
  const language = useGlobalStateSelector( ({ state }) => state.language || 'japanese' );
  const dispatch = useGlobalStateSelector( ({ dispatch }) => dispatch );
  const mnemonic = popup.body.mnemonic;
  const nativeAppAvailable = walletsAvailable({ hasCamera }).includes('nfcNative');

  // iPhone アプリで書き込む（設定画面のニーモニック欄と同じ 2 段）
  const nfcrwWrite = useNfcrwWrite({
    enabled: nativeAppAvailable,
    language,
    source: mnemonic,
    // レコードは Web NFC の形で作る（Web NFC の書き込みボタンと同じ）。アプリの形への変換はフックが行う
    buildRecords: async ({ intent }) => {
      if (intent === 'keycard') {
        const encryptedMnemonic = await encryptByKeyCardStyle({ pass, dispatch, plaintext: mnemonic, language });
        return formatToKeyCardStyle({ data: encryptedMnemonic });
      }
      else if (intent === 'plain') {
        return formatToSingleTextRecord({ data: mnemonic });
      }
      else {
        // useNfcrwWrite が書き込みを止め、onResult で失敗を知らせる
        throw new Error(`unknown intent: ${intent}`);
      }
    },
    onReadyToWrite: () => {
      dispatch({ type: 'setNotification', key: 'notification', value: words.readyToWriteEncryptedData[language] });
    },
    onResult: ({ ok, error }) => notifyNativeAppWriteResult({ dispatch, language, ok, error }),
  });

  const popupToDisplayAfterClose = {
    type: 'generalItems',
    body:
      <div className='widthMax marginTop1 padding0p5 preLine'>
        { words.cautionAboutSiteDataForMnemonic[language] }
      </div>,
  };

  useEffect( () => {
    return () => {
      window.setTimeout( () => {
        dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayer], value: popupToDisplayAfterClose });
      }, 0 );
    };
  }, [] );

  const writeInKeycardNfcFormatButton =
    <>
      <div className='widthMax marginTop1 '>
        { words.writeInKeyCardNfcFormat[language] }
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
        onClick={ async () => {
          dispatch({ type: 'setNotification', key: 'notification', value: words.touchWithNfcTag[language] });

          const encryptedMnemonic = await encryptByKeyCardStyle({ pass, dispatch, plaintext: mnemonic, language });
          const records = formatToKeyCardStyle({ data: encryptedMnemonic });
          writeNdefRecordsOnNfc({ dispatch, records, language });
        }}
      >
        <div>
          { words.write[language] }
        </div>
        <div>
          <img className='size2x2' src={iconKeyCard} alt='' />
        </div>
      </button>
    </>;

  const writeInPlainTextToNfcButton =
    <>
      <div className='widthMax marginTop1 '>
        { words.writeInPlainTextToNfc[language] }
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
        onClick={ () => {
          dispatch({ type: 'setNotification', key: 'notification', value: words.touchWithNfcTag[language] });

          const records = formatToSingleTextRecord({ data: mnemonic });
          writeNdefRecordsOnNfc({ dispatch, records, language });
        }}
      >
        <div>
          { words.write[language] }
        </div>
        <div>
          <img className='size2x2' src={iconKeyCard} alt='' />
        </div>
      </button>
    </>;

  const displayQrInKeycardBcFormatButton =
    <>
      <div className='widthMax marginTop1 '>
        { words.display2dBarcodeInKeyCardBcFormat[language] }
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
        onClick={ async () => {
          const encryptedMnemonic = await encryptByKeyCardStyle({ pass, dispatch, plaintext: mnemonic, language });

          const popup = {
            type: 'displayQrPopup',
            body: {
                data: encryptedMnemonic,
            },
          };

          dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });
        }}
      >
        <div>
          { words.display[language] }
        </div>
        <div>
          <img className='size2x2' src={iconQr} alt='' />
        </div>
      </button>
    </>;

  const displayQrInPlainTextButton =
    <>
      <div className='widthMax marginTop1 '>
        { words.display2dBarcodeInPlainText[language] }
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
        onClick={ () => {
          const popup = {
            type: 'displayQrPopup',
            body: {
                data: mnemonic,
            },
          };

          dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });
        }}
      >
        <div>
          { words.display[language] }
        </div>
        <div>
          <img className='size2x2' src={iconQr} alt='' />
        </div>
      </button>
    </>;

  const displayMnemonicInPlainTextButton =
    <>
      <div className='widthMax marginTop1 '>
        { words.displayMnemonicInText[language] }
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
        onClick={ () => {
          const popup = {
            type: 'generalItems',
            body: mnemonic,
          };

          dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });
        }}
      >
        <div>
          { words.display[language] }
        </div>
      </button>
    </>;

  const writeInKeyCardNfcFormatWithNativeAppButton =
    <NativeAppWriteButton
      nfcrwWrite={nfcrwWrite}
      intent='keycard'
      label={words.writeInKeyCardNfcFormatWithNativeApp[language]}
    />;

  const writeInPlainTextToNfcWithNativeAppButton =
    <NativeAppWriteButton
      nfcrwWrite={nfcrwWrite}
      intent='plain'
      label={words.writeInPlainTextToNfcWithNativeApp[language]}
    />;

  let writeButtons = [ displayMnemonicInPlainTextButton ];

  for (const wallet of walletsAvailable({ hasCamera })) {
    if (wallet === 'nfc') {
      writeButtons.push(writeInKeycardNfcFormatButton, writeInPlainTextToNfcButton);
    }
    else if (wallet === 'nfcNative') {
      writeButtons.push(writeInKeyCardNfcFormatWithNativeAppButton, writeInPlainTextToNfcWithNativeAppButton);
    }
    else if (wallet === 'qr') {
      writeButtons.push(displayQrInKeycardBcFormatButton, displayQrInPlainTextButton);
    }
  }


  return (
    <div className='flexColumn alignItemsCenter borderKOM marginTop1 padding0p5'>
      <div className='widthMax colorRed marginTop1 '>
        { words.cautionAboutMnemonic[language] }
      </div>
      <div className='widthMax colorRed marginTop1 '>
        { words.pleaseBeCarefulOfYourSurroundingsSoThatOthersCannotSee[language] }
      </div>
      <div className='flexColumn alignItemsCenter'>
        { writeButtons }
      </div>
    </div>
  );
}

// UNREGISTER ADDRESS
function UnregisterAddress() {
  const unregisteredAddress = useGlobalStateSelector( ({ state }) => state.unregisteredAddress );
  const keyPairsInPoolAll = useGlobalStateSelector( ({ state }) => state.keyPairs );
  const passkey = useGlobalStateSelector( ({ state }) => state.passkey );
  const network = useGlobalStateSelector( ({ state }) => state.networkMona );
  const language = useGlobalStateSelector( ({ state }) => state.language );
  const dispatch = useGlobalStateSelector( ({ dispatch }) => dispatch );


  return (
    <div className='flexColumn alignItemsCenter borderKOM marginTop1 padding0p5'>
      <div className='flexColumn alignItemsFlexStart widthMax marginTop1 '>
        <div className=''>
          { words.keybox[language] }
        </div>
        <div className=''>
          { words.unregisterMasterAddress[language] }
        </div>
      </div>
      <div className='flexRow justifyContentCenter alignItemsCenter marginTop0p5'>
        <SelectAddressFromKeypoolSection
          stateKeys={['unregisteredAddress', 'address']}
          stateKeysToSetAddress={stateKeysToSetAddress}
          stateKeysToSetUserName={stateKeysToSetUserName}
          loginEnabled={false}
          subAddressEnabled={false}
        />
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' tabindex='0'
        disabled={ passkey.busy }
        onClick={ async () => {
          try {
            dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'busy'], value: true });

            await handleClickUnregisterAddress({
              dispatch,
              keyPairsInPoolAll,
              unregisteredAddress,
              network,
              language
            });

            dispatch({ type: 'setNotification', key: 'notification', value: words.succeededToUnregisterAMasterAddress[language] });
          }
          finally {
            dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'busy'], value: false });
          }
        }}
      >
        <div>
          { words.unregisterMasterAddress[language] }
        </div>
        <img className='size2x2 margin0p5' src={iconKeyPool} alt='' />
      </button>
    </div>
  );
}

// UNREGISTER CREDENTIAL
function UnregisterCredential() {
  const language = useGlobalStateSelector( ({ state }) => state.language );
  const passkey = useGlobalStateSelector( ({ state }) => state.passkey );
  const dispatch = useGlobalStateSelector( ({ dispatch }) => dispatch );


  return (
    <div className='flexColumn alignItemsCenter borderKOM marginTop1 padding0p5'>
      <div className='flexColumn alignItemsFlexStart widthMax marginTopBottom1 '>
        <div className=''>
          { words.keybox[language] }
        </div>
        <div className=''>
          { words.unregisterWebauthnCredential[language] }
        </div>
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' tabindex='0'
        disabled={ passkey.busy }
        onClick={ async () => {
          try {
            dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'busy'], value: true });
            await handleClickUnregisterWebauthnCredential({ dispatch, language });

            dispatch({ type: 'setNotification', key: 'notification', value: words.succeededToUnregisterAPasskeyCredential[language] });
          }
          finally {
            dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'busy'], value: false });
          }
        }}
      >
        <div>
          { words.unregisterWebauthnCredential[language] }
        </div>
      </button>
    </div>
  );
}

// SAVE PASSWORD ENCRYPTED SECRETS
function SavePasswordEncryptedSecrets() {
  const popupLayerNext = 0;
  const savePasswordEncryptedMnemonicKeysState = useGlobalStateSelector( ({ state }) => state.savePasswordEncryptedMnemonic );
  const user = useGlobalStateSelector( ({ state }) => state.user );
  const login = useGlobalStateSelector( ({ state }) => state.login );
  const walletType = useGlobalStateSelector( ({ state }) => state.walletType );
  const session = useGlobalStateSelector( ({ state }) => state.session );
  const marginOfSessionTime = useGlobalStateSelector( ({ state }) => state.marginOfSessionTime );
  const assetInfos = useGlobalStateSelector( ({ state }) => state.assetInfo );
  const monacards = useGlobalStateSelector( ({ state }) => state.monacard );
  const clientParameters = useGlobalStateSelector( ({ state }) => state.config.clientParameters );
  const passkey = useGlobalStateSelector( ({ state }) => state.passkey );
  const network = useGlobalStateSelector( ({ state }) => state.networkMona );
  const language = useGlobalStateSelector( ({ state }) => state.language );
  const dispatch = useGlobalStateSelector( ({ dispatch }) => dispatch );

  // パスワード表現タイプ選択ボタン

  const kanaButton =
  <button className={'box3 focusEffect01 riseOut2 ' + (savePasswordEncryptedMnemonicKeysState.passwordExpressionType === 'kana' ? 'colorRed borderSelected' : 'borderNone')}
    tabindex='0'
    onClick={ () => {
      dispatch({type: 'setStateMultiLayers', keys: ['savePasswordEncryptedMnemonic', 'passwordExpressionType'], value: 'kana'});
    }}
  >
    <div className='paddingTopBottom0p5 ' >
      {words.kana[language]}
    </div>
  </button>;

  const romajiButton =
  <button className={'box3 focusEffect01 riseOut2 ' + (savePasswordEncryptedMnemonicKeysState.passwordExpressionType === 'romaji' ? 'colorRed borderSelected' : 'borderNone')}
    tabindex='0'
    onClick={ () => {
      dispatch({type: 'setStateMultiLayers', keys: ['savePasswordEncryptedMnemonic', 'passwordExpressionType'], value: 'romaji'});
    }}
  >
    <div className='paddingTopBottom0p5 ' >
      {words.romaji[language]}
    </div>
  </button>;

  const passwordExpressionTypeSelectButtons = [ kanaButton, romajiButton ];

  // 単語数選択プルダウンリスト

  const wordCountSelectList = [
    <option value='4'>4</option>,
    <option value='5'>5</option>,
    <option value='6'>6</option>,
    <option value='7'>7</option>,
    <option value='8'>8</option>,
    <option value='9'>9</option>,
    <option value='10'>10</option>,
    <option value='11'>11</option>,
    <option value='12'>12</option>,
  ];

  const wordCountSelect =
  <select
    className='borderKOM paddingLeft0p5 paddingRight0p5'
    style={{ width: '4em', textAlign: 'right' }}
    defaultValue='5'
    onChange={ (e) => {
      dispatch({type: 'setStateMultiLayers', keys: ['savePasswordEncryptedMnemonic', 'wordCount'], value: e.target.value});
    }}
  >
    { wordCountSelectList }
  </select>;


  return (
    <div className='flexColumn alignItemsCenter borderKOM marginTop1 padding0p5'>
      <div className='flexColumn alignItemsFlexStart widthMax marginTop1 '>
        <div className=''>
          { words.keybox[language] }
        </div>
        <div className=''>
          { words.savePasswordEncryptedSecrets[language] }
        </div>
        <div className='flexRow justifyContentFlexEnd alignItemsCenter widthMax marginTop1 '>
          <button className='borderNone backgroundColorTransparent cursor borderRadius2 riseOut2 marginSide0p2 ' tabindex='0'
            onClick={ () => {
              dispatch({ type: 'setStateMultiLayers', keys: ['savePasswordEncryptedMnemonic', 'password'], value: '' });
            }}
          >
            {words.clear[language]}
          </button>
        </div>
      </div>
      <div className='flexRow justifyContentSpaceAround widthMax '>
        { passwordExpressionTypeSelectButtons }
      </div>
      <div className='flexRow justifyContentCenter alignItemsCenter marginTopBottom1 '>
        <div className='marginSide0p5 '>
          { words.wordCount[language] }
        </div>
        <div className='marginSide0p5 '>
          { wordCountSelect }
        </div>
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' tabindex='0'
        onClick={ async () => {
          try {
            const password = await generateDicewarePassphraseAsync({
              words: savePasswordEncryptedMnemonicKeysState.wordCount,
              column: savePasswordEncryptedMnemonicKeysState.passwordExpressionType,
              separator: "　",
              csvPath: "/diceware-ja.csv",
            });

            dispatch({type: 'setStateMultiLayers', keys: ['savePasswordEncryptedMnemonic', 'password'], value: password});
          }
          catch (error) {
            const message = notificationMessageWithError(words.failedToGenerateAPassword[language], error);
            dispatch({ type: 'setNotification', key: 'notification', value: message });
            throw wrapErrorWithCause('failedToGenerateAPassword', error);
          }
        }}
      >
        <div>
          { words.generatePassword[language] }
        </div>
      </button>
      <div className='flexColumn alignItemsFlexStart width90PC borderKOM marginTopBottom1 padding0p5'>
        { savePasswordEncryptedMnemonicKeysState.password }
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' tabindex='0'
        disabled={ passkey.busy || !savePasswordEncryptedMnemonicKeysState.password }
        onClick={ async () => {
          try {
            dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'busy'], value: true });

            const {
              decryptedMnemonicRecords: mnemonicRecords,
              decryptedWifRecords: wifRecords,
              keyPairs,
            } = await handleClickAuthenticate({ dispatch, network, language });

            const secretRecordsByAddress = new Map(
              [ ...mnemonicRecords, ...wifRecords ].map( (secretRecord) => [ secretRecord.address, secretRecord ] )
            );
            const keyPairsToLogin = Array.from(secretRecordsByAddress.values())
            .filter( ( secretRecord ) => user[secretRecord.address]?.basicInformation === undefined )
            .map( ( secretRecord ) => secretRecord.keyPair );

            if (keyPairsToLogin.length >= 1) {
              handleClickLogin({
                dispatch,
                keyPairs: keyPairsToLogin,
                sessionToOverride: {},
                login,
                walletType,
                session,
                marginOfSessionTime,
                assetInfos,
                monacards,
                network,
                clientParameters,
                language,
              });
            }

            const passwordBackupRecordByAddress = new Map();

            for (const mnemonicRecord of mnemonicRecords) {
              passwordBackupRecordByAddress.set(mnemonicRecord.address, mnemonicRecord);
            }

            for (const wifRecord of wifRecords) {
              const existingRecord = passwordBackupRecordByAddress.get(wifRecord.address);

              if (existingRecord) {
                passwordBackupRecordByAddress.set(wifRecord.address, {
                  ...existingRecord,
                  wif: wifRecord.wif,
                });
              }
              else {
                passwordBackupRecordByAddress.set(wifRecord.address, wifRecord);
              }
            }

            const passwordBackupRecords = Array.from(passwordBackupRecordByAddress.values());

            if (passwordBackupRecords.length === 0) {
              dispatch({ type: 'setNotification', key: 'notification', value: words.noPasswordBackupAddressesFound[language] });
              throw new Error('No password backup addresses found');
            }

            // ユーザーはアドレスを一度だけ選択し、対応する秘密情報をまとめて保存する
            const { mnemonic, wif } = await new Promise( (resolve, reject) => {
              const popup = {
                type: 'selectSecretPopup',
                body: {
                  secretRecords: passwordBackupRecords,
                  resolve,
                  reject,
                },
              };

              dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });
            });

            // 選択したアドレスにニーモニックがある場合は先に保存する
            if (mnemonic) {
              await savePasswordEncryptedMnemonic({
                dispatch,
                mnemonic,
                password: savePasswordEncryptedMnemonicKeysState.password,
                network,
                language
              });
            }

            // 選択したアドレスに保存済みWIFがある場合だけ保存する
            if (wif) {
              await savePasswordEncryptedWif({
                dispatch,
                wif,
                password: savePasswordEncryptedMnemonicKeysState.password,
                network,
                language,
              });
            }

            dispatch({ type: 'setNotification', key: 'notification', value: words.succeededToSavePasswordEncryptedSecrets[language] });

            keepKeyPairsInMemory({ dispatch, keyPairs, periodToKeepKeyPairs: clientParameters.periodToKeepKeyPairs, language, network });
          }
          finally {
            dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'busy'], value: false });
          }
        }}
      >
        <div>
          { words.savePasswordEncryptedSecrets[language] }
        </div>
      </button>
    </div>
  );
}

// LOAD PASSWORD ENCRYPTED SECRETS
function LoadPasswordEncryptedSecrets() {
  const loadPasswordEncryptedMnemonicKeysState = useGlobalStateSelector( ({ state }) => state.loadPasswordEncryptedMnemonic );
  const network = useGlobalStateSelector( ({ state }) => state.networkMona );
  const language = useGlobalStateSelector( ({ state }) => state.language );
  const passkey = useGlobalStateSelector( ({ state }) => state.passkey );
  const dispatch = useGlobalStateSelector( ({ dispatch }) => dispatch );


  return (
    <div className='flexColumn alignItemsCenter borderKOM marginTop1 padding0p5'>
      <div className='flexColumn alignItemsFlexStart widthMax marginTop1 '>
        <div className=''>
          { words.keybox[language] }
        </div>
        <div className=''>
          { words.loadPasswordEncryptedSecrets[language] }
        </div>
        <div className='flexRow justifyContentFlexEnd alignItemsCenter widthMax marginTop1 '>
          <button className='borderNone backgroundColorTransparent cursor borderRadius2 riseOut2 marginSide0p2 ' tabindex='0'
            onClick={ () => {
              dispatch({ type: 'setStateMultiLayers', keys: ['loadPasswordEncryptedMnemonic', 'password'], value: '' });
            }}
          >
            {words.clear[language]}
          </button>
        </div>
      </div>
      <div className='flexRow justifyContentCenter alignItemsCenter marginTopBottom1 '>
          <TextLine3
            fieldName='loadPasswordEncryptedMnemonicPassword'
            keys={['loadPasswordEncryptedMnemonic', 'password']}
            // type='setStateMultiLayersMasked'
            face={words.password[language]}
            tooltip={false}
          />
      </div>
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' tabindex='0'
        disabled={ passkey.busy }
        onClick={ async () => {
          try {
            dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'busy'], value: true });
            await loadPasswordEncryptedSecrets({ dispatch, password: loadPasswordEncryptedMnemonicKeysState.password, network, language });

            dispatch({ type: 'setStateMultiLayers', keys: ['loadPasswordEncryptedMnemonic', 'password'], value: '' });
          }
          finally {
            dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'busy'], value: false });
          }
        }}
      >
        <div>
          { words.loadPasswordEncryptedSecrets[language] }
        </div>
      </button>
    </div>
  );
}


// HANDLE CLICK REGISTER INITIAL
async function handleClickRegisterInitial({ dispatch, mnemonic: mnemonicArg, configure, network, language }) {
  const now = new Date();
  const signatureVersion = '1';
  const userName = configure.userName;
  const acceptedVersionOfTheTermsAndConditions = configure.acceptedVersionOfTheTermsAndConditions;
  const acceptedVersionOfPrivacyPolicy = configure.acceptedVersionOfPrivacyPolicy;

  const purpose = 'initialRegistration';
  const clientTime = now.getTime();

  // 入力チェック

  if (userName === undefined || userName === null || userName === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.userNameMustBeFilled[language] });
    throw new Error('userName is missing');
  }

  if (acceptedVersionOfTheTermsAndConditions === undefined || acceptedVersionOfTheTermsAndConditions === null || acceptedVersionOfTheTermsAndConditions === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.acceptTheTermsAndConditions[language] });
    throw new Error('acceptedVersionOfTheTermsAndConditions is missing');
  }

  if (acceptedVersionOfPrivacyPolicy === undefined || acceptedVersionOfPrivacyPolicy === null || acceptedVersionOfPrivacyPolicy === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.acceptThePrivacyPolicy[language] });
    throw new Error('acceptedVersionOfPrivacyPolicy is missing');
  }

  // ---  ニーモニック生成 ---

  const { mnemonic, keyPairs, address } = generateMnemonic({ dispatch, mnemonicArg, network, language });

  // ---  登録オプションを取得 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const { options, token } = await getWebAuthnRegistrationOptions({ dispatch, purpose, clientTime, userNameRequested: userName, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  //  ---  challengeを書き換え（challengeOriginal || SHA256(address)）

  await overrideWebAuthnChallenge({ dispatch, options, address, language });

  // ---  認証器でWebAuthnアテステーションを作成 ---

  const attestationResponse = await makeWebAuthnAttestation({ dispatch, options, language });

  // ---  クリプト署名 ---

  const { cborB64Url: publicKeyCborB64Url } = await spkiPemToCoseCbor({ dispatch, spkiPem: attestationResponse.response.publicKey, language });

  const messageObject = {
    purpose: 'initialRegistration',
    address,
    webauthnPublicKey: publicKeyCborB64Url,
    signatureVersion,
    clientTime: now.getTime(),
  };

  const signature = signWithKeyNW({ dispatch, keyPair: keyPairs[0], message: canonicalize(messageObject), network, language });
  devLog('webauthnPublicKey', attestationResponse.response.publicKey);
  devLog('publicKeyCborB64Url', publicKeyCborB64Url);

  const symmetricKeyRequest = { address, signatureVersion, clientTime, signature, addressName: userName };

  // ---  サーバで検証・共通鍵生成・登録 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const registrationResponse = await requestWebAuthnRegistration({ dispatch, token, attestationResponse, symmetricKeyRequest, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  // ---  共通鍵でニーモニックを暗号化 ---

  const {
    ciphertextBase64: encryptedMnemonicBase64,
    ivHex: mnemonicIvHex,
  } = await encryptWithSymmetricKey({ dispatch, plaintext: mnemonic, symmetricKeyBase64: registrationResponse.symmetricKey, language });

  // ---  暗号化ニーモニックを保存 ---

  await saveEncryptedMnemonicInIndexedDb({ dispatch, encryptedMnemonicBase64, mnemonicIvHex, webAuthnResponse: registrationResponse, language });


  return { mnemonic, keyPairs: [ keyPairs[0] ], address };
}

// HANDLE CLICK REGISTER INITIAL FROM WIF
async function handleClickRegisterInitialFromWif({ dispatch, wif, configure, network, language }) {
  const now = new Date();
  const signatureVersion = '1';
  const userName = configure.userName;
  const acceptedVersionOfTheTermsAndConditions = configure.acceptedVersionOfTheTermsAndConditions;
  const acceptedVersionOfPrivacyPolicy = configure.acceptedVersionOfPrivacyPolicy;

  const purpose = 'initialRegistration';
  const clientTime = now.getTime();

  // 入力チェック

  if (userName === undefined || userName === null || userName === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.userNameMustBeFilled[language] });
    throw new Error('userName is missing');
  }

  if (acceptedVersionOfTheTermsAndConditions === undefined || acceptedVersionOfTheTermsAndConditions === null || acceptedVersionOfTheTermsAndConditions === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.acceptTheTermsAndConditions[language] });
    throw new Error('acceptedVersionOfTheTermsAndConditions is missing');
  }

  if (acceptedVersionOfPrivacyPolicy === undefined || acceptedVersionOfPrivacyPolicy === null || acceptedVersionOfPrivacyPolicy === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.acceptThePrivacyPolicy[language] });
    throw new Error('acceptedVersionOfPrivacyPolicy is missing');
  }

  // --- WIFからキーペア・アドレス導出 ---

  const { keyPair, address } = getKeyPairAndAddressFromWif({ dispatch, wif, network, language });

  // --- 登録オプションを取得 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const { options, token } = await getWebAuthnRegistrationOptions({ dispatch, purpose, clientTime, userNameRequested: userName, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  // --- challengeを書き換え（challengeOriginal || SHA256(address)）

  await overrideWebAuthnChallenge({ dispatch, options, address, language });

  // --- 認証器でWebAuthnアテステーションを作成 ---

  const attestationResponse = await makeWebAuthnAttestation({ dispatch, options, language });

  // --- クリプト署名 ---

  const { cborB64Url: publicKeyCborB64Url } = await spkiPemToCoseCbor({ dispatch, spkiPem: attestationResponse.response.publicKey, language });

  const messageObject = {
    purpose,
    address,
    webauthnPublicKey: publicKeyCborB64Url,
    signatureVersion,
    clientTime,
  };

  const signature = signWithKeyNW({ dispatch, keyPair, message: canonicalize(messageObject), network, language });
  devLog('webauthnPublicKey', attestationResponse.response.publicKey);
  devLog('publicKeyCborB64Url', publicKeyCborB64Url);

  const symmetricKeyRequest = { address, signatureVersion, clientTime, signature, addressName: userName };

  // --- サーバで検証・共通鍵生成・登録 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const registrationResponse = await requestWebAuthnRegistration({ dispatch, token, attestationResponse, symmetricKeyRequest, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  // --- 共通鍵でWIFを暗号化 ---

  const {
    ciphertextBase64: encryptedWifBase64,
    ivHex: wifIvHex,
  } = await encryptWithSymmetricKey({ dispatch, plaintext: wif, symmetricKeyBase64: registrationResponse.symmetricKey, language });

  // --- 暗号化WIFを保存 ---

  await saveEncryptedWifInIndexedDb({ dispatch, encryptedWifBase64, wifIvHex, webAuthnResponse: registrationResponse, language });


  return { wif, keyPairs: [ keyPair ], address };
}

// HANDLE CLICK AUTHENTICATE
async function handleClickAuthenticate({ dispatch, network, language }) {
  const now = new Date();

  // --- 任意: 非RK運用で userId 指定が必要な場合はここで差し込む（空ならRK既定）
  // const userId = ''; // 例: フォームや状態から取得。空文字ならサーバ側は allowCredentials を付けません（RK運用）。 

  const purpose = 'authentication';
  const clientTime = now.getTime();

  // --- 環境チェック

  if (!('PublicKeyCredential' in window)) {
    dispatch({ type: 'setNotification', key: 'notification', value: 'このブラウザはPasskeyに対応していません。' });
    return;
  }

  // --- サーバから認証用 options を取得（既定で op='authenticate'）

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const { options, token } = await getWebAuthnAuthenticationOptions({ dispatch, purpose, clientTime, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  // --- 認証器でWebAuthnアサーションを作成 ---
  // これだけでOK（内部で b64url→Uint8Array, navigator.credentials.get 呼び出し, 結果の整形）

  const assertionResponse = await makeWebAuthnAssertion({ dispatch, options, language });

  // const assertionResponse = await startAuthentication({ optionsJSON: options });

  // --- サーバへ検証リクエスト

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const verificationResponse = await requestWebAuthnAuthentication({ dispatch, token, assertionResponse, purpose, clientTime, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  devLog('Passkey sign-in OK:', JSON.stringify(verificationResponse));

  // --- 暗号化ニーモニックを取得

  const indexedDbRecords = await loadEncryptedMnemonicFromIndexedDb({ dispatch, verificationResponse, language });

  // --- 暗号化された秘密情報を復号

  const {
    decryptedMnemonicRecords,
    decryptedWifRecords,
    keyPairs,
    addresses,
  } = await decryptSecretsWithSymmetricKey({ dispatch, indexedDbRecords, symmetricKeys: verificationResponse.symmetricKeys, network, language });

  if (keyPairs.length > 0) {
    devLog('Decrypted secrets by passkey authentication', JSON.stringify(addresses));
    devLog('mnemonicRecords', decryptedMnemonicRecords.length);
    devLog('wifRecords', decryptedWifRecords.length);
    devLog('keyPairs', keyPairs.length);
    devLog('addresses', addresses.length);
  }

  return { decryptedMnemonicRecords, decryptedWifRecords, keyPairs, addresses };
}

// HANDLE CLICK REGISTER ADDITIONAL
async function handleClickRegisterAdditional({ dispatch, mnemonic: mnemonicArg, configure, network, language }) {
  const now = new Date();
  const signatureVersion = '1';
  const userName = configure.userName;
  const acceptedVersionOfTheTermsAndConditions = configure.acceptedVersionOfTheTermsAndConditions;
  const acceptedVersionOfPrivacyPolicy = configure.acceptedVersionOfPrivacyPolicy;

  // 入力チェック

  if (userName === undefined || userName === null || userName === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.userNameMustBeFilled[language] });
    throw new Error('userName is missing');
  }

  if (acceptedVersionOfTheTermsAndConditions === undefined || acceptedVersionOfTheTermsAndConditions === null || acceptedVersionOfTheTermsAndConditions === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.acceptTheTermsAndConditions[language] });
    throw new Error('acceptedVersionOfTheTermsAndConditions is missing');
  }

  if (acceptedVersionOfPrivacyPolicy === undefined || acceptedVersionOfPrivacyPolicy === null || acceptedVersionOfPrivacyPolicy === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.acceptThePrivacyPolicy[language] });
    throw new Error('acceptedVersionOfPrivacyPolicy is missing');
  }

  const purpose = 'additionalRegistration';
  const clientTime = now.getTime();

  // --- ニーモニック生成 ---

  const { mnemonic, keyPairs, address } = generateMnemonic({ dispatch, mnemonicArg, network, language });

  // --- 認証オプションを取得 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const { options, token } = await getWebAuthnAuthenticationOptions({ dispatch, purpose, clientTime, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  //  --- challengeを書き換え（challengeOriginal || SHA256(address)）

  await overrideWebAuthnChallenge({ dispatch, options, address, language });

  // --- 認証器でWebAuthnアサーションを作成 ---

  const assertionResponse = await makeWebAuthnAssertion({ dispatch, options, language });

  // const assertionResponse = await startAuthentication({ optionsJSON: options });

  // --- クリプト署名 ---

  const messageObject = {
    purpose,
    address,
    credentialId: assertionResponse.id,
    signatureVersion,
    clientTime,
  };

  const signature = signWithKeyNW({ dispatch, keyPair: keyPairs[0], message: canonicalize(messageObject), network, language });
  console.log('credentialId', assertionResponse.id);

  const cryptoAssertion = {
    address,
    signatureVersion,
    clientTime,
    signature,
    addressName: userName ?? address,
  };

  // --- サーバで検証・共通鍵生成・登録 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const verificationResponse = await requestWebAuthnAuthentication({ dispatch, token, assertionResponse, purpose, clientTime, cryptoAssertion, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  devLog('Passkey sign-in OK:', JSON.stringify(verificationResponse));

  // --- 共通鍵でニーモニックを暗号化 ---

  const {
    ciphertextBase64: encryptedMnemonicBase64,
    ivHex: mnemonicIvHex,
  } = await encryptWithSymmetricKey({ dispatch, plaintext: mnemonic, symmetricKeyBase64: verificationResponse.symmetricKey, language });

  // --- 暗号化ニーモニックを保存 ---

  await saveEncryptedMnemonicInIndexedDb({ dispatch, encryptedMnemonicBase64, mnemonicIvHex, webAuthnResponse: verificationResponse, language });


  return { mnemonic, keyPairs: [ keyPairs[0] ], address };
}

// HANDLE CLICK REGISTER ADDITIONAL FROM WIF
async function handleClickRegisterAdditionalFromWif({ dispatch, wif, configure, network, language }) {
  const now = new Date();
  const signatureVersion = '1';
  const userName = configure.userName;
  const acceptedVersionOfTheTermsAndConditions = configure.acceptedVersionOfTheTermsAndConditions;
  const acceptedVersionOfPrivacyPolicy = configure.acceptedVersionOfPrivacyPolicy;

  // 入力チェック

  if (userName === undefined || userName === null || userName === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.userNameMustBeFilled[language] });
    throw new Error('userName is missing');
  }

  if (acceptedVersionOfTheTermsAndConditions === undefined || acceptedVersionOfTheTermsAndConditions === null || acceptedVersionOfTheTermsAndConditions === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.acceptTheTermsAndConditions[language] });
    throw new Error('acceptedVersionOfTheTermsAndConditions is missing');
  }

  if (acceptedVersionOfPrivacyPolicy === undefined || acceptedVersionOfPrivacyPolicy === null || acceptedVersionOfPrivacyPolicy === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.acceptThePrivacyPolicy[language] });
    throw new Error('acceptedVersionOfPrivacyPolicy is missing');
  }

  const purpose = 'additionalRegistration';
  const clientTime = now.getTime();

  // --- WIFからキーペア・アドレス導出 ---

  const { keyPair, address } = getKeyPairAndAddressFromWif({ dispatch, wif, network, language });

  // --- 認証オプションを取得 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const { options, token } = await getWebAuthnAuthenticationOptions({ dispatch, purpose, clientTime, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  // --- challengeを書き換え（challengeOriginal || SHA256(address)）

  await overrideWebAuthnChallenge({ dispatch, options, address, language });

  // --- 認証器でWebAuthnアサーションを作成 ---

  const assertionResponse = await makeWebAuthnAssertion({ dispatch, options, language });

  // --- クリプト署名 ---

  const messageObject = {
    purpose,
    address,
    credentialId: assertionResponse.id,
    signatureVersion,
    clientTime,
  };

  const signature = signWithKeyNW({ dispatch, keyPair, message: canonicalize(messageObject), network, language });

  const cryptoAssertion = {
    address,
    signatureVersion,
    clientTime,
    signature,
    addressName: userName ?? address,
  };

  // --- サーバで検証・共通鍵生成・登録 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const verificationResponse = await requestWebAuthnAuthentication({ dispatch, token, assertionResponse, purpose, clientTime, cryptoAssertion, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  // --- 共通鍵でWIFを暗号化 ---

  const {
    ciphertextBase64: encryptedWifBase64,
    ivHex: wifIvHex,
  } = await encryptWithSymmetricKey({ dispatch, plaintext: wif, symmetricKeyBase64: verificationResponse.symmetricKey, language });

  // --- 暗号化WIFを保存 ---

  await saveEncryptedWifInIndexedDb({ dispatch, encryptedWifBase64, wifIvHex, webAuthnResponse: verificationResponse, language });


  return { wif, keyPairs: [ keyPair ], address };
}

// HANDLE CLICK UNREGISTER ADDRESS
async function handleClickUnregisterAddress({ dispatch, keyPairsInPoolAll, unregisteredAddress, network, language }) {
  const signatureVersion = '1';
  const now = new Date();

  const purpose = 'addressUnregistration';
  const clientTime = now.getTime();

  // --- キーペア取得 ---

  if (unregisteredAddress.address === undefined || unregisteredAddress.address === null || unregisteredAddress.address === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.addressIsRequired[language] });
    return;
  }

  const address = unregisteredAddress.address;
  devLog('address', address);

  if (keyPairsInPoolAll[address] === undefined || keyPairsInPoolAll[address] === null) {
    dispatch({ type: 'setNotification', key: 'notification', value: words.loginWithTheAddressFilld[language] });
    return;
  }

  const keyPair = keyPairsInPoolAll[address].keyPair;
  devLog('keyPair', keyPair);

  // --- 認証オプションを取得 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const { options, token } = await getWebAuthnAuthenticationOptions({ dispatch, purpose, clientTime, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  //  --- challengeを書き換え（challengeOriginal || SHA256(address)）

  await overrideWebAuthnChallenge({ dispatch, options, address, language });

  // --- 認証器でWebAuthnアサーションを作成 ---

  const assertionResponse = await makeWebAuthnAssertion({ dispatch, options, language });

  // const assertionResponse = await startAuthentication({ optionsJSON: options });

  // --- クリプト署名 ---

  const messageObject = {
    purpose,
    address,
    credentialId: assertionResponse.id,
    signatureVersion,
    clientTime,
  };

  const signature = signWithKeyNW({ dispatch, keyPair, message: canonicalize(messageObject), network, language });

  const cryptoAssertion = {
    address,
    signatureVersion,
    clientTime,
    signature,
  };

  // --- サーバで検証・アドレス登録解除 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const verificationResponse = await requestWebAuthnAuthentication({ dispatch, token, assertionResponse, purpose, clientTime, cryptoAssertion, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  devLog('Passkey address unregistration OK:', JSON.stringify(verificationResponse));

  // --- 暗号化ニーモニックを削除 ---

  await deleteEncryptedMnemonicFromIndexedDb({ dispatch, verificationResponse, language });
}

// HANDLE CLICK UNREGISTER WEBAUTHN CREDENTIAL
async function handleClickUnregisterWebauthnCredential({ dispatch, language }) {
  const now = new Date();

  const purpose = 'webauthnCredentialUnregistration';
  const clientTime = now.getTime();

  // --- 認証オプションを取得 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const { options, token } = await getWebAuthnAuthenticationOptions({ dispatch, purpose, clientTime, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  // --- 認証器でWebAuthnアサーションを作成 ---

  const assertionResponse = await makeWebAuthnAssertion({ dispatch, options, language });

  // const assertionResponse = await startAuthentication({ optionsJSON: options });

  // --- サーバで検証・WebAuthnクレデンシャル登録解除 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const verificationResponse = await requestWebAuthnAuthentication({ dispatch, token, assertionResponse, purpose, clientTime, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  devLog('Passkey webauthn credential unregistration OK:', JSON.stringify(verificationResponse));
}

// SAVE PASSWORD ENCRYPTED MNEMONIC
async function savePasswordEncryptedMnemonic({ dispatch, mnemonic, password, network, language }) {
  const now = new Date();
  const signatureVersion = '1';

  const purpose = 'passwordEncryptedMnemonicSaving';
  const clientTime = now.getTime();

  // --- パスワードでニーモニックを暗号化 ---

  const {
    ciphertextBase64: passwordEncryptedMnemonicBase64,
    ivHex: passwordEncryptedMnemonicIvHex,
  } = await encryptWithPassword({
    dispatch,
    plaintext: mnemonic,
    password,
    language
  });

  // --- キーペア・アドレス導出 ---

  const keyPairs = getKeyPairsFromMnemonic({ dispatch, network, mnemonic, language });
  const keyPair = keyPairs[0];
  const address = getAddressFromKeyNW({ dispatch, keyPair, language, network });

  // --- 認証オプションを取得 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const { options, token } = await getWebAuthnAuthenticationOptions({ dispatch, purpose, clientTime, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  //  --- challengeを書き換え（challengeOriginal || SHA256(address)）

  await overrideWebAuthnChallenge({ dispatch, options, address, language });

  // --- 認証器でWebAuthnアサーションを作成 ---

  const assertionResponse = await makeWebAuthnAssertion({ dispatch, options, language });

  // --- クリプト署名 ---

  console.log('credentialId', assertionResponse.id);

  const messageObject = {
    purpose,
    address,
    credentialId: assertionResponse.id,
    passwordEncryptedMnemonic: passwordEncryptedMnemonicBase64,
    passwordEncryptedMnemonicIv: passwordEncryptedMnemonicIvHex,
    signatureVersion,
    clientTime,
  };

  const signature = signWithKeyNW({ dispatch, keyPair, message: canonicalize(messageObject), network, language });

  const cryptoAssertion = {
    address,
    signatureVersion,
    clientTime,
    signature,
    // addressName: 'hoge' + address,
    passwordEncryptedMnemonic: passwordEncryptedMnemonicBase64,
    passwordEncryptedMnemonicIv: passwordEncryptedMnemonicIvHex,
  };

  // --- サーバで検証・パスワード暗号化ニーモニック保存 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const verificationResponse = await requestWebAuthnAuthentication({ dispatch, token, assertionResponse, purpose, clientTime, cryptoAssertion, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  devLog('Passkey sign-in OK:', JSON.stringify(verificationResponse));
}

// SAVE PASSWORD ENCRYPTED WIF
async function savePasswordEncryptedWif({ dispatch, wif, password, network, language }) {
  const now = new Date();
  const signatureVersion = '1';
  const purpose = 'passwordEncryptedWifSaving';
  const clientTime = now.getTime();

  // --- パスワードでWIFを暗号化 ---

  const {
    ciphertextBase64: passwordEncryptedWifBase64,
    ivHex: passwordEncryptedWifIvHex,
  } = await encryptWithPassword({
    dispatch,
    plaintext: wif,
    password,
    language,
  });

  // --- キーペア・アドレス導出 ---

  const { keyPair, address } = getKeyPairAndAddressFromWif({ dispatch, wif, network, language });

  // --- 認証オプションを取得 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const { options, token } = await getWebAuthnAuthenticationOptions({ dispatch, purpose, clientTime, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  // --- challengeを書き換え（challengeOriginal || SHA256(address)）

  await overrideWebAuthnChallenge({ dispatch, options, address, language });

  // --- 認証器でWebAuthnアサーションを作成 ---

  const assertionResponse = await makeWebAuthnAssertion({ dispatch, options, language });

  // --- 暗号化WIFを含むメッセージへ署名 ---

  const messageObject = {
    purpose,
    address,
    credentialId: assertionResponse.id,
    passwordEncryptedWif: passwordEncryptedWifBase64,
    passwordEncryptedWifIv: passwordEncryptedWifIvHex,
    signatureVersion,
    clientTime,
  };

  const signature = signWithKeyNW({ dispatch, keyPair, message: canonicalize(messageObject), network, language });

  const cryptoAssertion = {
    address,
    signatureVersion,
    clientTime,
    signature,
    passwordEncryptedWif: passwordEncryptedWifBase64,
    passwordEncryptedWifIv: passwordEncryptedWifIvHex,
  };

  // --- サーバで検証・パスワード暗号化WIF保存 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const verificationResponse = await requestWebAuthnAuthentication({ dispatch, token, assertionResponse, purpose, clientTime, cryptoAssertion, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  devLog('Password encrypted WIF saving OK:', JSON.stringify(verificationResponse));
}

// LOAD PASSWORD ENCRYPTED SECRETS
async function loadPasswordEncryptedSecrets({ dispatch, password, network, language }) {
  const now = new Date();

  const purpose = 'passwordEncryptedMnemonicLoading';
  const clientTime = now.getTime();

  // --- 認証オプションを取得 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const { options, token } = await getWebAuthnAuthenticationOptions({ dispatch, purpose, clientTime, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  // --- 認証器でWebAuthnアサーションを作成 ---

  const assertionResponse = await makeWebAuthnAssertion({ dispatch, options, language });

  // const assertionResponse = await startAuthentication({ optionsJSON: options });

  // --- サーバで検証・パスワード暗号化ニーモニック取得 ---

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const verificationResponse = await requestWebAuthnAuthentication({ dispatch, token, assertionResponse, purpose, clientTime, language })
  .finally(() => {
    dispatch({ type: 'setState', key: 'accessing', value: false });
  });

  devLog('Passkey sign-in OK:', JSON.stringify(verificationResponse));

  // --- パスワードで秘密情報を復号 ---

  const passwordEncryptedMnemonicInfos = verificationResponse.passwordEncryptedMnemonicInfos;
  const passwordEncryptedWifInfos = verificationResponse.passwordEncryptedWifInfos;
  const passwordNormalized = password.replace(/ /g, '　');

  if (!Array.isArray(passwordEncryptedMnemonicInfos) || !Array.isArray(passwordEncryptedWifInfos)) {
    throw new Error('password encrypted secret infos are missing');
  }

  let loadedSecretCount = 0;
  let skippedSecretCount = 0;
  let lastDecryptionError;

  for (const passwordEncryptedMnemonicInfo of passwordEncryptedMnemonicInfos) {
    let mnemonic;

    try {
      mnemonic = await decryptWithPassword({
        ciphertextBase64: passwordEncryptedMnemonicInfo.passwordEncryptedMnemonic,
        ivHex: passwordEncryptedMnemonicInfo.passwordEncryptedMnemonicIv,
        password: passwordNormalized,
      });
    }
    catch (error) {
      // 同じクレデンシャルに別のパスワードで暗号化された秘密情報がある場合はスキップする
      skippedSecretCount += 1;
      lastDecryptionError = error;
      continue;
    }

    const keyPairs = getKeyPairsFromMnemonic({ dispatch, network, mnemonic, language });
    const derivedAddress = getAddressFromKeyNW({ dispatch, keyPair: keyPairs[0], language, network });

    if (derivedAddress !== passwordEncryptedMnemonicInfo.address) {
      throw new Error('password encrypted mnemonic derived address mismatch');
    }

    const {
      ciphertextBase64: encryptedMnemonicBase64,
      ivHex: mnemonicIvHex,
    } = await encryptWithSymmetricKey({
      dispatch,
      plaintext: mnemonic,
      symmetricKeyBase64: passwordEncryptedMnemonicInfo.symmetricKey,
      language,
    });

    await saveEncryptedMnemonicInIndexedDb({
      dispatch,
      encryptedMnemonicBase64,
      mnemonicIvHex,
      webAuthnResponse: {
        address: passwordEncryptedMnemonicInfo.address,
        addressName: passwordEncryptedMnemonicInfo.addressName,
        userHandle: verificationResponse.userHandle,
      },
      language,
    });

    loadedSecretCount += 1;
  }

  for (const passwordEncryptedWifInfo of passwordEncryptedWifInfos) {
    let wif;

    try {
      wif = await decryptWithPassword({
        ciphertextBase64: passwordEncryptedWifInfo.passwordEncryptedWif,
        ivHex: passwordEncryptedWifInfo.passwordEncryptedWifIv,
        password: passwordNormalized,
      });
    }
    catch (error) {
      // 同じクレデンシャルに別のパスワードで暗号化された秘密情報がある場合はスキップする
      skippedSecretCount += 1;
      lastDecryptionError = error;
      continue;
    }

    const { address: derivedAddress } = getKeyPairAndAddressFromWif({ dispatch, wif, network, language });

    if (derivedAddress !== passwordEncryptedWifInfo.address) {
      throw new Error('password encrypted wif derived address mismatch');
    }

    const {
      ciphertextBase64: encryptedWifBase64,
      ivHex: wifIvHex,
    } = await encryptWithSymmetricKey({
      dispatch,
      plaintext: wif,
      symmetricKeyBase64: passwordEncryptedWifInfo.symmetricKey,
      language,
    });

    await saveEncryptedWifInIndexedDb({
      dispatch,
      encryptedWifBase64,
      wifIvHex,
      webAuthnResponse: {
        address: passwordEncryptedWifInfo.address,
        addressName: passwordEncryptedWifInfo.addressName,
        userHandle: verificationResponse.userHandle,
      },
      language,
    });

    loadedSecretCount += 1;
  }

  if (loadedSecretCount === 0 && skippedSecretCount > 0) {
    dispatch({ type: 'setNotification', key: 'notification', value: words.failedToDecrypt[language] });
    throw lastDecryptionError;
  }

  const loadedMessage = skippedSecretCount > 0
    ? `${words.succeededToLoadSomeSecrets[language]} (${loadedSecretCount}/${loadedSecretCount + skippedSecretCount})`
    : words.succeededToLoadSecrets[language];

  dispatch({ type: 'setNotification', key: 'notification', value: loadedMessage });
}

// PEM (-----BEGIN PUBLIC KEY----- ... -----END PUBLIC KEY-----) → ArrayBuffer(DER)
function pemToArrayBuffer(pem) {
  const base64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/g, "")
    .replace(/-----END PUBLIC KEY-----/g, "")
    .replace(/\s+/g, "");
  const bin = atob(base64);
  const buf = new ArrayBuffer(bin.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i++) {
    view[i] = bin.charCodeAt(i);
  }
  return buf;
}

// 入力（PEM 文字列 or Base64URL/Base64 文字列）を DER(ArrayBuffer) に正規化する
function toSpkiDer(input) {
  if (/-----BEGIN PUBLIC KEY-----/.test(input)) {
    return pemToArrayBuffer(input);
  }
  // PEMじゃない場合は「SPKIをBase64/Base64URLで渡してきた」とみなす
  return b64urlToUint8(input.trim()).buffer;
}

// RFC 8410 の Ed25519 SPKI っぽいかどうかをざっくり判定
// OID 1.3.101.112 = 06 03 2B 65 70
function looksLikeEd25519Spki(der) {
  for (let i = 0; i <= der.length - 5; i++) {
    if (
      der[i] === 0x06 &&
      der[i + 1] === 0x03 &&
      der[i + 2] === 0x2b &&
      der[i + 3] === 0x65 &&
      der[i + 4] === 0x70
    ) {
      return true;
    }
  }
  return false;
}

// RFC 8410形式のEd25519 SPKI DER から生の32バイト公開鍵を抜き出す
// （BIT STRING の中身を取り出す）
function extractEd25519RawFromSpkiDER(der) {
  // der は Uint8Array
  // 探したい構造:
  //   SEQUENCE {
  //     SEQUENCE { OID 1.3.101.112 }
  //     BIT STRING <00><32-byte public key>
  //   }
  //
  // ここでは後ろから BIT STRING (0x03) を探して長さを読む簡易実装。
  for (let i = der.length - 1; i >= 0; i--) {
    if (der[i] === 0x03 /* BIT STRING tag */) {
      const lenIdx = i + 1;
      if (lenIdx >= der.length) break;

      let len = der[lenIdx];
      let head = lenIdx + 1;

      // 長さが long form の場合の処理
      if (len & 0x80) {
        const n = len & 0x7f;
        len = 0;
        for (let j = 0; j < n; j++) {
          len = (len << 8) | der[head + j];
        }
        head += n;
      }

      // BIT STRING の最初の1バイトは「未使用ビット数」。Ed25519は0のはず。
      if (der[head] !== 0x00) {
        throw new Error("Unexpected unused-bits value in BIT STRING");
      }

      // body が公開鍵本体
      const bodyStart = head + 1;
      const bodyLen = len - (bodyStart - (lenIdx + 1));
      const body = der.slice(bodyStart, bodyStart + bodyLen);

      if (body.length !== 32) {
        throw new Error("Ed25519 public key length must be 32 bytes");
      }

      return body;
    }
  }

  // フォールバック（雑に末尾32バイトを返す）
  return der.slice(der.length - 32);
}

// 入力は "-----BEGIN PUBLIC KEY-----..." みたいなPEMでも、
// "MCowBQYDK2VwAyEA..." みたいなBase64URL文字列でもOK。
// 返り値は JWK (kty/crv/x... などのオブジェクト)。
export async function spkiPemToJwk(spkiInput) {
  const spkiDer = toSpkiDer(spkiInput);

  // 1) Ed25519（WebCryptoがサポートしていればここで拾える）
  try {
    const edKey = await crypto.subtle.importKey(
      "spki",
      spkiDer,
      { name: "Ed25519" },
      true,
      ["verify"]
    );
    const jwk = await crypto.subtle.exportKey("jwk", edKey);
    // 期待される形:
    // { kty: "OKP", crv: "Ed25519", x: "...", ext: true, key_ops: ["verify"] }
    return jwk;
  } catch (e1) {
    // 続行
  }

  // 2) ECDSA P-256 (ES256)
  try {
    const ecKey = await crypto.subtle.importKey(
      "spki",
      spkiDer,
      { name: "ECDSA", namedCurve: "P-256" },
      true,
      ["verify"]
    );
    const jwk = await crypto.subtle.exportKey("jwk", ecKey);
    // { kty:"EC", crv:"P-256", x:"...", y:"...", ... }
    return jwk;
  } catch (e2) {
    // 続行
  }

  // 3) RSA (RSASSA-PKCS1-v1_5 with SHA-256, a.k.a. RS256相当)
  try {
    const rsaKey = await crypto.subtle.importKey(
      "spki",
      spkiDer,
      {
        name: "RSASSA-PKCS1-v1_5",
        hash: "SHA-256",
      },
      true,
      ["verify"]
    );
    const jwk = await crypto.subtle.exportKey("jwk", rsaKey);
    // { kty:"RSA", n:"...", e:"...", ... }
    return jwk;
  } catch (e3) {
    // 続行
  }

  // 4) 最後のフォールバック：
  // WebCryptoがEd25519未サポートでも、SPKIがEd25519(RFC8410)っぽければ
  // DERを直接パースしてJWKを組み立てる。
  {
    const derU8 = new Uint8Array(spkiDer);
    if (looksLikeEd25519Spki(derU8)) {
      const raw = extractEd25519RawFromSpkiDER(derU8); // 32 bytes Uint8Array

      const jwk = {
        kty: "OKP",
        crv: "Ed25519",
        x: bufferToB64url(raw),
        ext: true,
        key_ops: ["verify"],
      };
      return jwk;
    }
  }

  throw new Error(
    "Unsupported or unrecognized SPKI public key (not Ed25519/P-256/RSA, or malformed)"
  );
}

// 2. JWK -> COSE Key object {1:...,3:..., -1:...,-2:...,-3:...}
//    and CBORエンコード( Uint8Array )まで返す
function jwkToCoseCbor(jwk) {
  // --- 1. Ed25519 (OKP / EdDSA) ---
  //
  // JWK例:
  // {
  //   kty: "OKP",
  //   crv: "Ed25519",
  //   x: "<base64url of 32-byte public key>"
  // }
  //
  // COSE (OKP / Ed25519) の典型マッピング:
  //   1: kty      = 1      (OKP)
  //   3: alg      = -8     (EdDSA)
  //        -8 は "EdDSA" 汎用。最近は -19 = Ed25519 を使う実装もある。
  //   -1: crv     = 6      (Ed25519)
  //   -2: x (pub) = 32-byte public key
  //
  if (jwk.kty === "OKP" && jwk.crv === "Ed25519") {
    const xBytes = b64urlToUint8(jwk.x); // 32-byte public key

    const coseKey = {
      1: 1,        // kty: OKP
      3: -8,       // alg: EdDSA (you could also choose -19 for Ed25519-specific)
      "-1": 6,     // crv: Ed25519
      "-2": xBytes // x: public key bytes
    };

    const cborBytes = encodeCborCoseKey(coseKey);
    return {
      coseKey,
      cborBytes,
      cborB64Url: bufferToB64url(cborBytes),
    };
  }

  // --- 2. P-256 (ES256 / ECDSA over P-256) ---
  //
  // 入力JWKイメージ:
  // {
  //   kty: "EC",
  //   crv: "P-256",
  //   x: "...",  // base64url
  //   y: "..."
  // }
  //
  // COSE EC2 key:
  //   1: kty      = 2 (EC2)
  //   3: alg      = -7 (ES256)
  //  -1: crv      = 1 (P-256)
  //  -2: x
  //  -3: y
  //
  if (jwk.kty === "EC" && jwk.crv === "P-256") {
    const xBytes = b64urlToUint8(jwk.x);
    const yBytes = b64urlToUint8(jwk.y);

    const coseKey = {
      1: 2,      // kty: EC2
      3: -7,     // alg: ES256
      "-1": 1,   // crv: P-256
      "-2": xBytes,
      "-3": yBytes,
    };

    const cborBytes = encodeCborCoseKey(coseKey);
    return {
      coseKey,
      cborBytes,
      cborB64Url: bufferToB64url(cborBytes),
    };
  }

  // --- 3. RSA (RS256) ---
  //
  // 入力JWKイメージ:
  // {
  //   kty: "RSA",
  //   n: "...", // modulus (base64url)
  //   e: "..."  // exponent (base64url)
  // }
  //
  // COSE RSA key:
  //   1: kty      = 3 (RSA)
  //   3: alg      = -257 (RS256)
  //  -1: n (modulus)
  //  -2: e (exponent)
  //
  if (jwk.kty === "RSA") {
    const nBytes = b64urlToUint8(jwk.n);
    const eBytes = b64urlToUint8(jwk.e);

    const coseKey = {
      1: 3,        // kty: RSA
      3: -257,     // alg: RS256
      "-1": nBytes,
      "-2": eBytes,
    };

    const cborBytes = encodeCborCoseKey(coseKey);
    return {
      coseKey,
      cborBytes,
      cborB64Url: bufferToB64url(cborBytes),
    };
  }

  throw new Error("Unsupported JWK type for COSE conversion (supported: Ed25519 OKP, P-256 EC, RSA)");
}

// Minimal CBOR encoder for the specific COSE key map we build.
// We only need: CBOR map (major type 5), negative int (major type 1),
// byte string (major type 2), and small ints (major type 0).
//
// This is intentionally tiny and not a general-purpose CBOR encoder.
// It assumes:
// - keys are small integers (like 1, 3, -1, -2, -3)
// - values are either small ints, negative small ints, or Uint8Array.
function encodeCborCoseKey(coseMapObj) {
  // coseMapObj is a plain object like {1:2,3:-7,"-1":1,"-2":Uint8Array,...}
  // We'll convert keys to an array of [intKey, value] pairs.
  const entries = Object.entries(coseMapObj).map(([k, v]) => [parseInt(k, 10), v]);

  // Encode map header: major type 5, length = number of pairs
  const chunks = [];
  chunks.push(encodeCborMapHeader(entries.length));

  for (const [key, value] of entries) {
    chunks.push(encodeCborInt(key));
    chunks.push(encodeCborValue(value));
  }

  return concatUint8(chunks);
}

function encodeCborMapHeader(len) {
  // major type 5 (map), additional info = len
  return encodeCborMajorTypeWithLen(5, len);
}

function encodeCborMajorTypeWithLen(major, len) {
  if (len < 24) {
    return Uint8Array.of((major << 5) | len);
  } else if (len < 256) {
    return Uint8Array.of((major << 5) | 24, len);
  } else if (len < 65536) {
    return Uint8Array.of((major << 5) | 25, len >> 8, len & 0xff);
  }
  throw new Error("len too large for this minimal encoder");
}

function encodeCborInt(num) {
  // Handle positive and negative separately.
  if (num >= 0) {
    // major type 0
    return encodeCborMajorTypeWithLen(0, num);
  } else {
    // CBOR negative integers use formula: value = -1 - n
    // So for n = -1 -> 0, n = -2 -> 1, etc.
    const encoded = -1 - num;
    return encodeCborMajorTypeWithLen(1, encoded);
  }
}

function encodeCborByteString(bytes) {
  // major type 2
  const header = encodeCborMajorTypeWithLen(2, bytes.length);
  return concatUint8([header, bytes]);
}

function encodeCborValue(v) {
  if (v instanceof Uint8Array) {
    return encodeCborByteString(v);
  }
  if (typeof v === "number") {
    return encodeCborInt(v);
  }
  throw new Error("Unsupported CBOR value type in this minimal encoder");
}

// 3. High-level helper: PEM(SPKI) -> COSE(CBOR)
async function spkiPemToCoseCbor({ dispatch, spkiPem, language }) {
  try {
    const jwk = await spkiPemToJwk(spkiPem);
    return jwkToCoseCbor(jwk);
  }
  catch (error) {
    const message = notificationMessageWithError(words.failedToConvertPublicKey[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToConvertSpkiPemToCoseCbor', error);
  }
}

// GET WEB AUTHN REGISTRATION OPTIONS
async function getWebAuthnRegistrationOptions({ dispatch, purpose, clientTime, userNameRequested, language }) {
  const API_BASE = 'https://' + process.env.REACT_APP_KB_API_DOMAIN;
  const OPTIONS_PATH = '/get_webauthn_options';
  let options, token;

  try {
    const optionsResponse = await postJson(
      `${API_BASE}${OPTIONS_PATH}`,
      {
        op: 'register',
        purpose,
        clientTime,
        userNameRequested,
        // userName,
      },
    );

    ({ options, token } = optionsResponse);
  }
  catch (error) {
    const message = notificationMessageWithError(words.failedToGetPasskeyRegistrationOptions[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToGetWebAuthnRegistrationOptions', error);
  }

  return { options, token };
}

// GET WEB AUTHN AUTHENTICATION OPTIONS
async function getWebAuthnAuthenticationOptions({ dispatch, purpose, clientTime, language }) {
  const API_BASE = 'https://' + process.env.REACT_APP_KB_API_DOMAIN;
  const OPTIONS_PATH = '/get_webauthn_options';
  let responseOptions;

  try {
    responseOptions = await postJson(
      `${API_BASE}${OPTIONS_PATH}`,
      {
        op: 'authenticate',         // 認証（追加登録）
        purpose,
        clientTime,
        // userId: userId || undefined // 非RK時のみサーバがallowCredentialsを構成
      },
    );
  }
  catch (error) {
    // const message = String(error?.message || error);
    // let friendly;

    // if (message.includes('actionPayload')) {
    //   friendly = 'actionPayload が不正です。';
    // }
    // else if (message.includes('origin')) {
    //   friendly = 'Origin/CORS の設定を確認してください。';
    // }
    // else {
    //   friendly = '認証用 options の取得に失敗しました。';
    // }

    // dispatch({ type: 'setNotification', key: 'notification', value: `${friendly}: ${message}` });
    // dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'busy'], value: false });

    const message = notificationMessageWithError(words.failedToGetPasskeyAuthenticationOptions[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToGetWebAuthnAuthenticationOptions', error);
  }

  // サーバは { options, token, userName, userDisplayName } を返す想定
  const { options, token } = responseOptions;

  return { options, token };
}

// OVERRIDE WEB AUTHN CHALLENGE

async function overrideWebAuthnChallenge({dispatch, options, address, language}) {
  try {
    const challengeOriginalU8A = new Uint8Array(base64URLStringToBuffer(options.challenge));
    const addressHashU8A = await sha256Utf8(address);
    const challengeU8A = concatU8A(challengeOriginalU8A, addressHashU8A);
    options.challenge = bufferToBase64URLString(challengeU8A.buffer);
  }
  catch (error) {
    const message = notificationMessageWithError(words.failedToOverridePasskeyChallenge[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToOverrideWebAuthnChallenge', error);
  }

  return options;
}

// MAKE WEB AUTHN ATTESTATION
async function makeWebAuthnAttestation({ dispatch, options, language }) {
  let attestationResponse;

  try {
    // ※ クリック直後の同期チェーン内で呼ぶこと（ユーザーアクティベーションが必要）
    attestationResponse = await startRegistration({ optionsJSON: options });
  }
  catch (error) {
    const message = notificationMessageWithError(words.failedToMakePasskeyAuthenticationData[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToMakeWebAuthnAttestation', error);
  }

  return attestationResponse;
}

// MAKE WEB AUTHN ASSERTION
async function makeWebAuthnAssertion({ dispatch, options, language }) {
  let assertionResponse;

  try {
    assertionResponse = await startAuthentication({ optionsJSON: options });
  }
  catch (error) {
    const message = notificationMessageWithError(words.failedToMakePasskeyAuthenticationData[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToMakeWebAuthnAssertion', error);
  }

  return assertionResponse;
}

// REQUEST WEB AUTHN REGISTRATION
async function requestWebAuthnRegistration({ dispatch, token, attestationResponse, symmetricKeyRequest, language }) {
  const API_BASE = 'https://' + process.env.REACT_APP_KB_API_DOMAIN;
  const COMPLETE_PATH = '/verify_webauthn_registration';
  let registrationResponse;

  try {
    registrationResponse = await postJson(
      `${API_BASE}${COMPLETE_PATH}`,
      {
        token,
        attestationResponse,
        symmetricKeyRequest,
      },
    );
  } catch (error) {
    const message = notificationMessageWithError(words.failedToRegisterPasskey[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToRequestWebAuthnRegistration', error);
  }

  return registrationResponse;
}

// REQUEST WEB AUTHN AUTHENTICATION
async function requestWebAuthnAuthentication({ dispatch, token, assertionResponse, purpose, clientTime, cryptoAssertion, language }) {
  const API_BASE = 'https://' + process.env.REACT_APP_KB_API_DOMAIN;
  const COMPLETE_PATH = '/verify_webauthn_authentication';
  let verificationResponse;

  try {
    verificationResponse = await postJson(
      `${API_BASE}${COMPLETE_PATH}`,
      { token, assertionResponse, purpose, clientTime, cryptoAssertion },
    );
  }
  catch (error) {
    const message = notificationMessageWithError(words.failedToAuthenticateWithYourPasskey[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToRequestWebAuthnAuthentication', error);
  }

  if (!verificationResponse?.verified) {
    // verified: false の場合、エラーにしてるはずなので、ここまで来ないが念のため。

    dispatch({ type: 'setNotification', key: 'notification', value: words.failedToAuthenticateWithYourPasskey[language] });
    throw new Error('failedToRequestWebAuthnAuthentication');
  }

  return verificationResponse;
}

// ENCRYPT WITH SYMMETRIC KEY
async function encryptWithSymmetricKey({ dispatch, plaintext, symmetricKeyBase64, language }) {
  let ciphertextBase64;
  let ivHex;

  try {
    if (!symmetricKeyBase64) {
      throw new Error('invalid symmetric key response');
    }
    if (typeof plaintext !== 'string' || plaintext === '') {
      throw new Error('plaintext is missing');
    }

    const symmetricKeyBuffer = base64ToArrayBuffer(symmetricKeyBase64);
    const symmetricKey = await window.crypto.subtle.importKey(
      'raw',
      symmetricKeyBuffer,
      'AES-GCM',
      false,
      ['encrypt']
    );

    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      symmetricKey,
      new TextEncoder().encode(plaintext)
    ); // AuthTagも含まれている

    ciphertextBase64 = arrayBufferToBase64(encryptedBuffer);
    ivHex = uint8ArrayToHex(iv);

  }
  catch (error) {
    const message = notificationMessageWithError(words.failedToEncrypt[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToEncryptWithSymmetricKey', error);
  }

  return { ciphertextBase64, ivHex };
}

// DECRYPT SECRETS WITH SYMMETRIC KEY
async function decryptSecretsWithSymmetricKey({ dispatch, indexedDbRecords, symmetricKeys, network, language }) {
  const decryptedMnemonicRecords = [];
  const decryptedWifRecords = [];
  const keyPairByAddress = new Map();

  if (!Array.isArray(symmetricKeys)) {
    throw new TypeError('symmetricKeys must be an array');
  }
  if (!Array.isArray(indexedDbRecords)) {
    throw new TypeError('indexedDbRecords must be an array');
  }

  if (symmetricKeys.length === 0 || indexedDbRecords.length === 0) {
    return { decryptedMnemonicRecords, decryptedWifRecords, keyPairs: [], addresses: [] };
  }

  for (const symmetricKeyInfo of symmetricKeys) {
    const { address, symmetricKey: decryptedSymmetricKey } = symmetricKeyInfo ?? {};

    if (!address || !decryptedSymmetricKey) {
      continue;
    }

    const indexedDbRecord = indexedDbRecords.find((record) => record?.address === address);

    if (!indexedDbRecord) {
      continue;
    }

    if (indexedDbRecord.encryptedMnemonicBase64 && indexedDbRecord.mnemonicIvHex) {
      try {
        const decryptedMnemonic = await decryptWithSymmetricKey({
          ciphertextBase64: indexedDbRecord.encryptedMnemonicBase64,
          ivHex: indexedDbRecord.mnemonicIvHex,
          keyBase64: decryptedSymmetricKey,
        });
        const keyPairs = getKeyPairsFromMnemonic({ dispatch, network, mnemonic: decryptedMnemonic, language });
        const derivedAddress = getAddressFromKeyNW({ dispatch, keyPair: keyPairs[0], language, network });

        // 復号した秘密情報がサーバのアドレスに対応することを必ず確認する
        if (derivedAddress !== address) {
          console.error('mnemonic derived address mismatch', { addressFromServer: address });
        }
        else {
          decryptedMnemonicRecords.push({
            address,
            keyPair: keyPairs[0],
            mnemonic: decryptedMnemonic,
            addressName: indexedDbRecord.addressName,
          });

          if (!keyPairByAddress.has(address)) {
            keyPairByAddress.set(address, keyPairs[0]);
          }
        }
      }
      catch (error) {
        console.error('mnemonic decryption failed', error);
      }
    }

    if (indexedDbRecord.encryptedWifBase64 && indexedDbRecord.wifIvHex) {
      try {
        const decryptedWif = await decryptWithSymmetricKey({
          ciphertextBase64: indexedDbRecord.encryptedWifBase64,
          ivHex: indexedDbRecord.wifIvHex,
          keyBase64: decryptedSymmetricKey,
        });
        const { keyPair, address: derivedAddress } = getKeyPairAndAddressFromWif({
          dispatch,
          wif: decryptedWif,
          network,
          language,
        });

        // WIFが別アドレスの秘密鍵へ差し替えられていないことを確認する
        if (derivedAddress !== address) {
          console.error('wif derived address mismatch', { addressFromServer: address });
        }
        else {
          decryptedWifRecords.push({
            address,
            keyPair,
            wif: decryptedWif,
            addressName: indexedDbRecord.addressName,
          });

          if (!keyPairByAddress.has(address)) {
            keyPairByAddress.set(address, keyPair);
          }
        }
      }
      catch (error) {
        console.error('wif decryption failed', error);
      }
    }
  }

  return {
    decryptedMnemonicRecords,
    decryptedWifRecords,
    keyPairs: Array.from(keyPairByAddress.values()),
    addresses: Array.from(keyPairByAddress.keys()),
  };
}

// ENCRYPT WITH PASSWORD
async function encryptWithPassword({ dispatch, plaintext, password, language }) {
  let ciphertextBase64;
  let ivHex;

  try {
    if (typeof plaintext !== 'string' || plaintext === '') {
      throw new Error('plaintext is missing');
    }
    if (typeof password !== 'string' || password === '') {
      throw new Error('password is missing');
    }

    // パスワードをUint8Arrayに変換
    const passwordU8A = new TextEncoder().encode(password);

    // ソルトをUint8Arrayに変換
    const saltHexArray = ['00', '00', '00', '00', '00', '00', '00', '72'];
    const saltNumberArray = saltHexArray.map( hex => Number('0x' + hex) );
    const saltU8A = Uint8Array.from(saltNumberArray);

    // PBKDF2を使用して鍵を導出
    const keyMaterial = await window.crypto.subtle.importKey(
        "raw",
        passwordU8A,
        { name: "PBKDF2" },
        false,
        ["deriveKey"]
    );

    const deriveKeyAlgorithm = {
        name: "PBKDF2",
        salt: saltU8A,
        iterations: 6000000,
        hash: "SHA-256"
    };

    const key = await window.crypto.subtle.deriveKey(
        deriveKeyAlgorithm,
        keyMaterial,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt"]
    );

    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      key,
      new TextEncoder().encode(plaintext)
    ); // AuthTagも含まれている

    ciphertextBase64 = arrayBufferToBase64(encryptedBuffer);
    ivHex = uint8ArrayToHex(iv);
  }
  catch (error) {
    const message = notificationMessageWithError(words.failedToEncrypt[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToEncryptWithPassword', error);
  }

  return { ciphertextBase64, ivHex }
}

// DECRYPT WITH PASSWORD
async function decryptWithPassword({ ciphertextBase64, ivHex, password }) {
  let plaintext;

  try {
    const encryptedBuffer = base64ToArrayBuffer(ciphertextBase64);
    const ivU8A = hexStringToUint8Array(ivHex);

    // パスワードをUint8Arrayに変換
    const passwordU8A = new TextEncoder().encode(password);

    // ソルトをUint8Arrayに変換
    const saltHexArray = ['00', '00', '00', '00', '00', '00', '00', '72'];
    const saltNumberArray = saltHexArray.map( hex => Number('0x' + hex) );
    const saltU8A = Uint8Array.from(saltNumberArray);

    // PBKDF2を使用して鍵を導出
    const keyMaterial = await window.crypto.subtle.importKey(
        "raw",
        passwordU8A,
        { name: "PBKDF2" },
        false,
        ["deriveKey"]
    );

    const deriveKeyAlgorithm = {
        name: "PBKDF2",
        salt: saltU8A,
        iterations: 6000000,
        hash: "SHA-256"
    };

    const key = await window.crypto.subtle.deriveKey(
        deriveKeyAlgorithm,
        keyMaterial,
        { name: "AES-GCM", length: 256 },
        false,
        ["decrypt"]
    );

    const plaintextBuffer = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: ivU8A,
      },
      key,
      encryptedBuffer
    ); // AuthTagも含まれている
  
    plaintext = new TextDecoder().decode(plaintextBuffer);
  }
  catch (error) {
    throw wrapErrorWithCause('failedToDecryptWithPassword', error);
  }

  return plaintext;
}

// LOAD ENCRYPTED MNEMONIC FROM INDEXEDDB
async function loadEncryptedMnemonicFromIndexedDb({ dispatch, verificationResponse, language }) {
  const indexedDbRecords = [];

  if (verificationResponse?.userHandle && Array.isArray(verificationResponse?.symmetricKeys)) {
    for (const symmetricKeyInfo of verificationResponse.symmetricKeys) {
      if (!symmetricKeyInfo?.address) {
        continue;
      }

      try {
        const record = await getCompositeRecordFromIndexedDb({
          dbName: 'keybox',
          storeName: 'encryptedMnemonic',
          keyFields: ['userHandle', 'address'],
          keySource: {
            userHandle: verificationResponse.userHandle,
            address: symmetricKeyInfo.address,
          },
        });

        if (record) {
          indexedDbRecords.push(record);
        }
        // else {
        //   const allRecords = await getAllRecordsFromIndexedDb({
        //     dbName: 'keybox',
        //     storeName: 'encryptedMnemonic',
        //   });

        //   if (Array.isArray(allRecords) && allRecords.length > 0) {
        //     for (const item of allRecords) {
        //       const alreadyPushed = indexedDbRecords.some((existing) => existing?.userHandle === item?.userHandle && existing?.address === item?.address);

        //       if (!alreadyPushed) {
        //         indexedDbRecords.push(item);
        //       }
        //     }

        //     devLog('IndexedDB all records for encryptedMnemonic', JSON.stringify(allRecords));
        //   }
        //   else {
        //     indexedDbRecords.push(null);
        //   }
        // }
      }
      catch (error) {
        const message = notificationMessageWithError(words.failedToLoadEncryptedMnemonic[language], error);
        dispatch({ type: 'setNotification', key: 'notification', value: message });
        throw wrapErrorWithCause('failedToLoadEncryptedMnemonicFromIndexedDb', error);
      }
    }
  }

  if (indexedDbRecords.length > 0) {
    devLog('IndexedDB records for passkey authentication', JSON.stringify(indexedDbRecords));
    // dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'indexedDbRecords'], value: indexedDbRecords });
  }

  return indexedDbRecords;
}

// SAVE ENCRYPTED MNEMONIC IN INDEXEDDB
async function saveEncryptedMnemonicInIndexedDb({ dispatch, encryptedMnemonicBase64, mnemonicIvHex, webAuthnResponse, language }) {

  const compositeRecord = {
    userHandle: webAuthnResponse.userHandle,
    address: webAuthnResponse.address,
    encryptedMnemonicBase64,
    mnemonicIvHex,
    addressName: webAuthnResponse.addressName,
  };

  try {
    if (compositeRecord.userHandle && compositeRecord.address) {
      await updateCompositeRecordInIndexedDb({
        dbName: 'keybox',
        storeName: 'encryptedMnemonic',
        keyFields: ['userHandle', 'address'],
        keySource: {
          userHandle: compositeRecord.userHandle,
          address: compositeRecord.address,
        },
        changes: {
          encryptedMnemonicBase64: compositeRecord.encryptedMnemonicBase64,
          mnemonicIvHex: compositeRecord.mnemonicIvHex,
          addressName: compositeRecord.addressName,
        },
      });
    }
    else {
      throw new Error('userHandle/address missing for composite key');
    }
  } catch (error) {
    const message = notificationMessageWithError(words.failedToSaveEncryptedMnemonic[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToSaveEncryptedMnemonic', error);
  }

  return compositeRecord;
}

// SAVE ENCRYPTED WIF IN INDEXEDDB
async function saveEncryptedWifInIndexedDb({ dispatch, encryptedWifBase64, wifIvHex, webAuthnResponse, language }) {
  const compositeRecord = {
    userHandle: webAuthnResponse.userHandle,
    address: webAuthnResponse.address,
    encryptedWifBase64,
    wifIvHex,
    addressName: webAuthnResponse.addressName,
  };

  try {
    if (compositeRecord.userHandle && compositeRecord.address) {
      await updateCompositeRecordInIndexedDb({
        dbName: 'keybox',
        storeName: 'encryptedMnemonic',
        keyFields: ['userHandle', 'address'],
        keySource: {
          userHandle: compositeRecord.userHandle,
          address: compositeRecord.address,
        },
        changes: {
          encryptedWifBase64: compositeRecord.encryptedWifBase64,
          wifIvHex: compositeRecord.wifIvHex,
          addressName: compositeRecord.addressName,
        },
      });
    }
    else {
      throw new Error('userHandle/address missing for composite key');
    }
  }
  catch (error) {
    const message = notificationMessageWithError(words.failedToSaveEncryptedWif[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToSaveEncryptedWif', error);
  }

  return compositeRecord;
}

// DELETE ENCRYPTED MNEMONIC FROM INDEXEDDB
async function deleteEncryptedMnemonicFromIndexedDb({ dispatch, verificationResponse, language }) {

  const keySource = {
    userHandle: verificationResponse.userHandle,
    address: verificationResponse.address,
  };

  try {
    if (keySource.userHandle && keySource.address) {
      const deletionResult = await deleteCompositeRecordFromIndexedDb({
        dbName: 'keybox',
        storeName: 'encryptedMnemonic',
        keyFields: ['userHandle', 'address'],
        keySource,
      });

      return deletionResult;
    }
    else {
      throw new Error('userHandle/address missing for composite key');
    }
  }
  catch (error) {
    const message = notificationMessageWithError(words.failedToDeleteEncryptedMnemonic[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToDeleteEncryptedMnemonic', error);
  }
}

// DECRYPT WITH SYMMETRIC KEY
async function decryptWithSymmetricKey({ ciphertextBase64, ivHex, keyBase64 }) {
  if (!ciphertextBase64 || !ivHex || !keyBase64) {
    throw new Error('decryptWithSymmetricKey: arguments missing');
  }

  const encryptedBuffer = base64ToArrayBuffer(ciphertextBase64);
  const symmetricKeyBuffer = base64ToArrayBuffer(keyBase64);

  const symmetricKey = await window.crypto.subtle.importKey(
    'raw',
    symmetricKeyBuffer,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );

  const ivU8A = hexStringToUint8Array(ivHex);

  const decryptedBuffer = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: ivU8A,
    },
    symmetricKey,
    encryptedBuffer
  );

  return new TextDecoder().decode(decryptedBuffer);
}

// // TO PUBLIC KEY REQUEST OPTIONS
// function toPublicKeyRequestOptions(serverOptions) {
//   // サーバ生成の options を `navigator.credentials.get` 形式へ変換
//   // - challenge: Base64URL → ArrayBuffer
//   // - allowCredentials[].id: Base64URL → ArrayBuffer（非RK時のみ存在）
//   const out = { ...serverOptions };
// 
//   out.challenge = b64urlToUint8(serverOptions.challenge);
// 
//   if (Array.isArray(serverOptions.allowCredentials)) {
//     out.allowCredentials = serverOptions.allowCredentials.map((cred) => ({
//       ...cred,
//       id: b64urlToUint8(cred.id),
//     }));
//   }
//   // timeout / userVerification / rpId はサーバが設定済み
//   return out;
// }

// // TO ASSERTION RESPONSE PAYLOAD
// function toAssertionResponsePayload(cred) {
//   // navigator.credentials.get の戻りを、サーバが期待する JSON に整形
//   // サーバ側 verify は `assertionResponse` を受付（verifyAuthenticationResponseに渡す）
//   return {
//     id: cred.id,
//     rawId: bufferToB64url(cred.rawId),
//     type: cred.type,
//     response: {
//       clientDataJSON: bufferToB64url(cred.response.clientDataJSON),
//       authenticatorData: bufferToB64url(cred.response.authenticatorData),
//       signature: bufferToB64url(cred.response.signature),
//       userHandle: cred.response.userHandle
//         ? bufferToB64url(cred.response.userHandle)
//         : null,
//     },
//     clientExtensionResults:
//       typeof cred.getClientExtensionResults === 'function'
//         ? cred.getClientExtensionResults()
//         : {},
//     authenticatorAttachment: cred.authenticatorAttachment ?? null,
//   };
// }

function base64ToArrayBuffer(base64) {
  const binary = window.atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function uint8ArrayToHex(bytes) {
  return Array.from(bytes).map(byte => byte.toString(16).padStart(2, '0')).join('');
}

function hexStringToUint8Array(hexString) {
  if (!hexString || typeof hexString !== 'string') {
    throw new Error('hexString is required');
  }

  const normalized = hexString.trim();

  if (normalized.length % 2 !== 0) {
    throw new Error('hexString must have even length');
  }

  const bytes = new Uint8Array(normalized.length / 2);

  for (let i = 0; i < normalized.length; i += 2) {
    bytes[i / 2] = parseInt(normalized.substr(i, 2), 16);
  }

  return bytes;
}

// B64URL TO UINT8
function b64urlToUint8(b64u) {
  const s = String(b64u).replace(/-/g, '+').replace(/_/g, '/');
  if (s.length % 4 === 1) throw new TypeError('Invalid base64url string');
  const padded = s + '==='.slice((s.length + 3) % 4);
  return Uint8Array.from(atob(padded), c => c.charCodeAt(0));
}

// BUFFER TO B64URL
function bufferToB64url(buf) {
  const u8 =
    buf instanceof ArrayBuffer
      ? new Uint8Array(buf)
      : new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);

  let bin = '';
  const CHUNK = 0x8000; // 32768

  for (let i = 0; i < u8.length; i += CHUNK) {
    bin += String.fromCharCode(...u8.subarray(i, i + CHUNK));
  }

  // 0–255 をそのまま 0x00–0xFF の"バイナリ文字列"に
  // const bin = new TextDecoder('latin1').decode(u8); // 'iso-8859-1' でも可

  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/,'');
}

function concatUint8(arrays) {
  let totalLen = 0;
  for (const a of arrays) totalLen += a.length;
  const out = new Uint8Array(totalLen);
  let offset = 0;
  for (const a of arrays) {
    out.set(a, offset);
    offset += a.length;
  }
  return out;
}

// CONCAT U8A
function concatU8A(a, b) {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

// SHA256 UTF8
async function sha256Utf8(string) {
  const buf = new TextEncoder().encode(string);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return new Uint8Array(hash); // 32 bytes
}

const P256_SPKI_PREFIX = new Uint8Array([
  0x30, 0x59, 0x30, 0x13, 0x06, 0x07, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x02, 0x01, 0x06, 0x08, 0x2a,
  0x86, 0x48, 0xce, 0x3d, 0x03, 0x01, 0x07, 0x03, 0x42, 0x00,
]);

// eslint-disable-next-line no-unused-vars
function cosePublicKeyBase64UrlToSpkiBase64Url(cosePublicKeyB64Url) {
  if (typeof cosePublicKeyB64Url !== 'string' || !cosePublicKeyB64Url) {
    throw new Error('COSE public key must be a non-empty string');
  }

  const coseBuffer = base64URLStringToBuffer(cosePublicKeyB64Url);
  const state = { dataView: new DataView(coseBuffer), offset: 0 };

  function readUint(additional) {
    if (additional < 24) {
      return additional;
    }

    if (additional === 24) {
      const value = state.dataView.getUint8(state.offset);
      state.offset += 1;
      return value;
    }

    if (additional === 25) {
      const value = state.dataView.getUint16(state.offset);
      state.offset += 2;
      return value;
    }

    if (additional === 26) {
      const value = state.dataView.getUint32(state.offset);
      state.offset += 4;
      return value;
    }

    throw new Error('Unsupported CBOR integer width');
  }

  function readValue() {
    const initial = state.dataView.getUint8(state.offset);
    state.offset += 1;
    const majorType = initial >> 5;
    const additional = initial & 0x1f;

    if (majorType === 0) {
      return readUint(additional);
    }

    if (majorType === 1) {
      return -1 - readUint(additional);
    }

    if (majorType === 2) {
      const length = readUint(additional);
      const bytes = new Uint8Array(state.dataView.buffer.slice(state.offset, state.offset + length));
      state.offset += length;
      return bytes;
    }

    if (majorType === 5) {
      const length = readUint(additional);
      const map = new Map();

      for (let index = 0; index < length; index += 1) {
        const key = readValue();

        if (typeof key !== 'number') {
          throw new Error('CBOR map key must be a number');
        }

        map.set(key, readValue());
      }

      return map;
    }

    throw new Error(`Unsupported CBOR major type: ${majorType}`);
  }

  const coseMap = readValue();

  if (!(coseMap instanceof Map)) {
    throw new Error('COSE public key is not encoded as a CBOR map');
  }

  const x = coseMap.get(-2);
  const y = coseMap.get(-3);

  if (!(x instanceof Uint8Array) || !(y instanceof Uint8Array)) {
    throw new Error('COSE public key is missing X or Y coordinate');
  }

  if (x.length !== 32 || y.length !== 32) {
    throw new Error('COSE public key coordinates must be 32 bytes each');
  }

  const uncompressedPoint = new Uint8Array(1 + x.length + y.length);
  uncompressedPoint[0] = 0x04;
  uncompressedPoint.set(x, 1);
  uncompressedPoint.set(y, 1 + x.length);

  const spkiDer = new Uint8Array(P256_SPKI_PREFIX.length + uncompressedPoint.length);
  spkiDer.set(P256_SPKI_PREFIX, 0);
  spkiDer.set(uncompressedPoint, P256_SPKI_PREFIX.length);

  return bufferToBase64URLString(spkiDer.buffer);
}

export {
   ConfirmMnemonicPopup,
   UnregisterAddress,
   UnregisterCredential,
   SavePasswordEncryptedSecrets,
   LoadPasswordEncryptedSecrets,
   handleClickRegisterInitial,
   handleClickRegisterInitialFromWif,
   handleClickAuthenticate,
   handleClickRegisterAdditional,
   handleClickRegisterAdditionalFromWif,
   handleClickUnregisterAddress,
   handleClickUnregisterWebauthnCredential,
   savePasswordEncryptedMnemonic,
   savePasswordEncryptedWif,
   loadPasswordEncryptedSecrets,
};
